import { useEffect, useRef, useState } from "react";
import { Film, Youtube, Upload, Link2, Loader2, AlertCircle, ChevronDown, RotateCw, HelpCircle } from "lucide-react";
import { http } from "@/lib/api";
import { logWarn } from "@/lib/log";
import { fmtDuration, tsToSeconds } from "@/lib/video";

const STEPS = { fetching: "Fetching video…", transcribing: "Transcribing…", watching: "Watching the screen…", summarising: "Summarising…" };
const SOURCE_ICON = { youtube: Youtube, upload: Upload };

function useVideo(id, onDone) {
  const [v, setV] = useState(null);
  const doneRef = useRef(onDone);
  doneRef.current = onDone;
  useEffect(() => {
    let alive = true, timer;
    const load = () => http.get(`/videos/${id}`).then((r) => {
      if (!alive) return;
      setV((prev) => {
        if (prev?.status === "pending" && r.data.status !== "pending") doneRef.current?.();
        return r.data;
      });
      if (r.data.status === "pending") timer = setTimeout(load, 3000);
    }).catch((e) => logWarn("Video status failed", e));
    load();
    return () => { alive = false; clearTimeout(timer); };
  }, [id]);
  return v;
}

function Transcript({ v }) {
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(null);
  const box = useRef(null);
  useEffect(() => {
    const h = (e) => {
      if (e.detail.videoId !== v.video_id) return;
      const sec = e.detail.seconds;
      const idx = Math.max(0, v.segments.findIndex((s, i) => s.start <= sec && (v.segments[i + 1]?.start ?? Infinity) > sec));
      setOpen(true);
      setActive(idx);
      setTimeout(() => box.current?.querySelector(`[data-seg="${idx}"]`)?.scrollIntoView({ block: "center", behavior: "smooth" }), 50);
    };
    window.addEventListener("video-seek", h);
    return () => window.removeEventListener("video-seek", h);
  }, [v]);
  return (
    <div className="mt-2 w-full">
      <button onClick={() => setOpen(!open)} data-testid={`video-transcript-toggle-${v.video_id}`}
        className="text-xs text-neutral-400 hover:text-white inline-flex items-center gap-1 transition-colors">
        <ChevronDown className={`h-3 w-3 transition-transform ${open ? "rotate-180" : ""}`} /> {open ? "Hide" : "Show"} transcript
      </button>
      {open && (
        <div ref={box} className="mt-2 max-h-72 overflow-y-auto scroll-thin rounded-xl border border-white/10 bg-black/30 p-3 space-y-1.5 text-left" data-testid={`video-transcript-${v.video_id}`}>
          {v.segments.map((s, i) => (
            <p key={`${s.start}-${i}`} data-seg={i} className={`text-xs leading-relaxed rounded px-1 ${i === active ? "bg-sky-400/15 text-white" : "text-neutral-300"}`}>
              <span className="font-mono text-sky-300 mr-2">{fmtDuration(s.start)}</span>{s.text}
            </p>
          ))}
        </div>
      )}
    </div>
  );
}

const btn = "inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full border border-white/10 text-neutral-300 hover:bg-white/10 hover:text-white transition-colors";

function Failed({ v }) {
  return (
    <div className="space-y-2">
      <p className="text-xs text-red-300 flex items-center gap-2" data-testid="video-status"><AlertCircle className="h-3 w-3 shrink-0" /> {v.error}</p>
      <div className="flex flex-wrap gap-2">
        <button className={btn} onClick={() => window.dispatchEvent(new Event("video-upload-pick"))} data-testid={`video-upload-instead-${v.video_id}`}>
          <Upload className="h-3 w-3" /> Upload the file instead
        </button>
        {v.kind === "link" && v.url && (
          <button className={btn} onClick={() => window.dispatchEvent(new CustomEvent("video-retry", { detail: { url: v.url } }))} data-testid={`video-retry-${v.video_id}`}>
            <RotateCw className="h-3 w-3" /> Try again
          </button>
        )}
        {v.kind === "link" && v.url && (
          <button className={btn} onClick={() => window.dispatchEvent(new CustomEvent("video-guide", { detail: { url: v.url } }))} data-testid={`video-howto-${v.video_id}`}>
            <HelpCircle className="h-3 w-3" /> How to download it
          </button>
        )}
      </div>
    </div>
  );
}

function Status({ v }) {
  if (!v || v.status === "pending") {
    return <p className="text-xs text-sky-200 flex items-center gap-2" data-testid="video-status"><Loader2 className="h-3 w-3 animate-spin" /> Watching your video… {STEPS[v?.step] || ""}</p>;
  }
  if (v.status === "failed") return <Failed v={v} />;
  return null;
}

export function VideoCard({ id, onDone }) {
  const v = useVideo(id, onDone);
  const Icon = SOURCE_ICON[v?.source] || (v?.kind === "link" ? Link2 : Film);
  return (
    <div className="w-full max-w-md rounded-2xl border border-white/10 bg-white/[0.04] p-3 text-left" data-testid={`video-card-${id}`}>
      <div className="flex gap-3">
        <div className="h-16 w-28 rounded-xl bg-sky-400/10 overflow-hidden shrink-0 flex items-center justify-center">
          {v?.thumbnail ? <img src={v.thumbnail} alt="" className="h-full w-full object-cover" /> : <Film className="h-6 w-6 text-sky-300" />}
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-sm text-neutral-100 line-clamp-2">{v?.title || "Video"}</p>
          <p className="text-[11px] text-neutral-500 flex items-center gap-1.5 mt-1"><Icon className="h-3 w-3" /> {v ? fmtDuration(v.duration) : ""} · {v?.source || ""}</p>
        </div>
      </div>
      <div className="mt-2"><Status v={v} /></div>
      {v?.visual_reason === "plan" && <p className="text-[11px] text-neutral-500 mt-1" data-testid="video-plan-note">Learned from speech only. On-screen understanding comes with Starter.</p>}
      {v?.visual_reason === "fetch" && <p className="text-[11px] text-neutral-500 mt-1">The picture couldn't be fetched, so this is based on the speech.</p>}
      {v?.status === "done" && v.segments?.length > 0 && <Transcript v={v} />}
    </div>
  );
}

export const seekVideo = (videoId, ts) => window.dispatchEvent(new CustomEvent("video-seek", { detail: { videoId, seconds: tsToSeconds(ts) } }));
